import React, { useState, useRef, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNotifications } from '@/hooks/useNotifications';
import { useSession } from '../providers/AuthProvider';

function NotificationBellBase() {
  const { user } = useSession();
  const { notifications, unreadCount, markAllRead } = useNotifications(user?.id);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handler(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  function handleOpen() {
    setOpen((o) => !o);
    if (!open && unreadCount > 0) markAllRead();
  }

  if (!user) return null;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={handleOpen}
        className="relative p-2 rounded-xl hover:bg-white/5 transition-colors"
        aria-label={`Notifikationer${unreadCount > 0 ? ` (${unreadCount} ulæste)` : ''}`}
        aria-expanded={open}
        type="button"
      >
        <Bell className="w-5 h-5 text-[var(--color-text-secondary)]" />
        {unreadCount > 0 && (
          <motion.span
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            className="absolute top-1 right-1 w-4 h-4 bg-[#0D6E8A] rounded-full text-[10px] font-bold text-white flex items-center justify-center"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </motion.span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.95 }}
            transition={{ duration: 0.15 }}
            style={{ position: 'fixed', top: '64px', right: '12px', width: 'min(320px, calc(100vw - 24px))' }}
            className="bg-[var(--color-surface)] border border-[var(--color-border)] rounded-2xl shadow-2xl overflow-hidden z-[9999]"
          >
            <div className="px-4 py-3 border-b border-[var(--color-border)] flex items-center justify-between">
              <span className="text-[var(--color-text)] font-semibold text-sm">Notifikationer</span>
              {unreadCount === 0 && notifications.length > 0 && (
                <span className="text-[var(--color-text-secondary)] text-xs">Alle læst</span>
              )}
            </div>

            <div className="max-h-96 overflow-y-auto divide-y divide-[var(--color-border)]">
              {notifications.length === 0 ? (
                <div className="py-8 text-center text-[var(--color-text-secondary)] text-sm">
                  Ingen notifikationer
                </div>
              ) : (
                notifications.map((n) => (
                  <div
                    key={n.id}
                    className={`px-4 py-3 transition-colors ${!n.read ? 'bg-[#0D6E8A]/10' : ''}`}
                  >
                    <p className="text-[var(--color-text)] text-sm font-medium leading-snug">{n.title}</p>
                    <p className="text-[var(--color-text-secondary)] text-xs mt-0.5 leading-relaxed">{n.body}</p>
                    <p className="text-[var(--color-text-secondary)] text-xs mt-1 opacity-60">
                      {new Date(n.created_at).toLocaleString('da-DK', {
                        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit',
                      })}
                    </p>
                  </div>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export const NotificationBell = React.memo(NotificationBellBase);
